import { useSyncExternalStore } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// One clock for the whole board.
//
// A KDS shows a running timer on every card. The obvious implementation — a
// setInterval in each card, or one in the board that bumps a state counter —
// re-renders the entire board once a second. With 40 cards, each with a dozen
// lines and a progress bar, that is 40 reconciliations per second on a £120
// Android tablet, and it shows: the board stutters when a cook scrolls, and
// taps get dropped because the main thread is busy.
//
// So there is exactly ONE interval in the app, and components subscribe to it
// through useSyncExternalStore. React then re-renders only the components that
// actually read the tick — in practice just the small <Elapsed> spans — and the
// card bodies stay untouched between real data changes.
//
// The tick is emitted as a whole second (Math.floor of the epoch), not the
// millisecond value, so a subscriber that renders "04:12" is not re-rendered
// 60 times within the second it spends showing "04:12".
// ─────────────────────────────────────────────────────────────────────────────

type Listener = () => void;

const listeners = new Set<Listener>();
let currentSecond = Math.floor(Date.now() / 1000);
let interval: ReturnType<typeof setInterval> | null = null;

function tick(): void {
  const next = Math.floor(Date.now() / 1000);
  if (next === currentSecond) return;
  currentSecond = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  if (!interval) {
    // 250ms, not 1000ms: a one-second interval drifts, and on a tablet that
    // throttles background timers it can drift by whole seconds. Sampling four
    // times a second and emitting only on a second boundary keeps the displayed
    // timer within ~250ms of the truth at negligible cost.
    interval = setInterval(tick, 250);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && interval) {
      clearInterval(interval);
      interval = null;
    }
  };
}

function getSnapshot(): number {
  return currentSecond;
}

/** Current epoch second, re-rendering the caller once per second. */
export function useSecond(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
